import { useState, useEffect, useContext } from 'react';
import toastError from '../../toasts/toastError';

import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';

import {
  Grid,
  Paper,
  Tab,
  Tabs,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  useTheme,
} from '@mui/material';

import QueueSelect from '../QueueSelect';
import { Can } from '../Can';
import TabPanel from '../TabPanel';
import AvatarUploader from '../AvatarUpload';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import useWhatsApps from '../../hooks/useWhatsApps';
import { getStyled } from './styles';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import toastSuccess from '../../toasts/toastSuccess';

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  password: Yup.string().min(5, 'Too Short!').max(50, 'Too Long!'),
  email: Yup.string().email('Invalid email').required('Required'),
  allHistoric: Yup.string().nullable(),
});

const UserModal = ({ open, onClose, userId }) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const initialState = {
    name: '',
    email: '',
    password: '',
    profile: 'user',
    startWork: '00:00',
    endWork: '23:59',
    farewellMessage: '',
    allTicket: 'disable',
    allowGroup: false,
    defaultTheme: 'dark',
    defaultMenu: 'closed',
    allHistoric: 'disabled',
    allUserChat: 'disabled',
    userClosePendingTicket: 'enabled',
    showDashboard: 'disabled',
    allowRealTime: 'disabled',
    allowConnections: 'disabled',
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  const { whatsApps } = useWhatsApps();
  const [tab, setTab] = useState('general');
  const [avatar, setAvatar] = useState(null);
  const { isAuth } = useContext(AuthContext);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => {
          return { ...prevState, ...data };
        });

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : '');
      } catch (err) {
        toastError(err);
      }
    };

    if (open && isAuth) {
      fetchUser();
    }
  }, [userId, open, isAuth]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSaveUser = async (values) => {
    const uploadAvatar = async (file) => {
      const formData = new FormData();
      formData.append('userId', file.id);
      formData.append('typeArch', 'user');
      formData.append('profileImage', avatar);

      const { data } = await api.post(
        `/users/${file.id}/media-upload`,
        formData,
      );

      localStorage.setItem('profileImage', data.user.profileImage);
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
    };
    try {
      if (userId) {
        const { data } = await api.put(`/users/${userId}`, userData);
        if (
          avatar &&
          (!user?.profileImage || !user?.profileImage !== avatar.name)
        )
          uploadAvatar(data);
      } else {
        await api.post('/users', userData);

        if (!user?.profileImage && avatar) uploadAvatar(user);
      }
      if (userId === loggedInUser.id) {
        handleClose();
        toastSuccess(i18n.t('userModal.success'), {
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
        window.location.reload();
      } else {
        handleClose();
        toastSuccess(i18n.t('userModal.success'), {
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
      }
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div style={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='sm'
        fullWidth
        scroll='paper'
        slotProps={{
          paper: {
            sx: { ...theme.custom.scrollbarStyles },
          },
        }}
      >
        <DialogTitle id='form-dialog-title' sx={theme.custom.dialogColor}>
          {userId
            ? `${i18n.t('userModal.title.edit')}`
            : `${i18n.t('userModal.title.add')}`}
        </DialogTitle>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => (
            <Form>
              <Paper elevation={1}>
                <Tabs
                  value={tab}
                  scrollButtons='on'
                  variant='scrollable'
                  onChange={handleTabChange}
                  sx={classes.tab}
                >
                  <Tab
                    label={i18n.t('userModal.tabs.general')}
                    value={'general'}
                  />
                  <Tab
                    label={i18n.t('userModal.tabs.permissions')}
                    value={'permissions'}
                  />
                </Tabs>
              </Paper>
              <Paper sx={classes.paper} elevation={0}>
                <DialogContent dividers sx={theme.custom.dialogColor}>
                  <TabPanel sx={classes.container} value={tab} name={'general'}>
                    <Grid
                      container
                      spacing={1}
                      alignContent='center'
                      alignItems='center'
                      justifyContent='center'
                    >
                      <FormControl sx={classes.updateDiv}>
                        <AvatarUploader
                          setAvatar={setAvatar}
                          avatar={user.profileImage}
                          companyId={user.companyId}
                        />
                        {user.profileImage && (
                          <Button
                            variant='outlined'
                            sx={theme.custom.editButton}
                            onClick={() => {
                              user.profileImage = null;
                              setFieldValue('profileImage', null);
                              setAvatar(null);
                            }}
                          >
                            {i18n.t('userModal.title.removeImage')}
                          </Button>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <Field
                          as={TextField}
                          label={i18n.t('userModal.form.name')}
                          autoFocus
                          name='name'
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant='outlined'
                          margin='dense'
                          fullWidth
                          sx={theme.custom.textField}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <Field
                          as={TextField}
                          label={i18n.t('userModal.form.password')}
                          type='password'
                          name='password'
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          variant='outlined'
                          margin='dense'
                          fullWidth
                          sx={theme.custom.textField}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 8, xl: 8 }}>
                        <Field
                          as={TextField}
                          label={i18n.t('userModal.form.email')}
                          name='email'
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          variant='outlined'
                          margin='dense'
                          fullWidth
                          sx={theme.custom.textField}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4, xl: 4 }}>
                        <FormControl
                          variant='outlined'
                          margin='dense'
                          fullWidth
                        >
                          <Can
                            role={loggedInUser.profile}
                            perform='user-modal:editProfile'
                            yes={() => (
                              <>
                                <InputLabel
                                  id='profile-selection-input-label'
                                  sx={theme.custom.label}
                                >
                                  {i18n.t('userModal.form.profile')}
                                </InputLabel>

                                <Field
                                  as={Select}
                                  label={i18n.t('userModal.form.profile')}
                                  name='profile'
                                  labelId='profile-selection-label'
                                  id='profile-selection'
                                  required
                                  sx={theme.custom.textField}
                                >
                                  <MenuItem value='admin'>Admin</MenuItem>
                                  <MenuItem value='user'>User</MenuItem>
                                </Field>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                        <Can
                          role={loggedInUser.profile}
                          perform='user-modal:editQueues'
                          yes={() => (
                            <QueueSelect
                              selectedQueueIds={selectedQueueIds}
                              onChange={(values) => setSelectedQueueIds(values)}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                        <Can
                          role={loggedInUser.profile}
                          perform='user-modal:editProfile'
                          yes={() => (
                            <FormControl
                              variant='outlined'
                              margin='dense'
                              sx={classes.maxWidth}
                              fullWidth
                            >
                              <InputLabel sx={theme.custom.label}>
                                {i18n.t('userModal.form.whatsapp')}
                              </InputLabel>
                              <Field
                                as={Select}
                                value={whatsappId}
                                onChange={(e) => setWhatsappId(e.target.value)}
                                label={i18n.t('userModal.form.whatsapp')}
                                sx={theme.custom.selectField}
                                MenuProps={theme.custom.menuProps}
                              >
                                <MenuItem value={''}>&nbsp;</MenuItem>
                                {whatsApps.map((whatsapp) => (
                                  <MenuItem
                                    key={whatsapp.id}
                                    value={whatsapp.id}
                                  >
                                    {whatsapp.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Can
                      role={loggedInUser.profile}
                      perform='user-modal:editProfile'
                      yes={() => (
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 12, sm: 6, xl: 6 }}>
                            <LocalizationProvider
                              dateAdapter={AdapterMoment}
                              adapterLocale='pt-br'
                            >
                              <TimePicker
                                value={
                                  values.startWork
                                    ? moment(values.startWork, 'HH:mm')
                                    : moment('23:59', 'HH:mm')
                                }
                                onChange={(newValue) => {
                                  setFieldValue(
                                    'startWork',
                                    newValue
                                      ? moment(newValue).format('HH:mm')
                                      : '23:59',
                                  );
                                }}
                                label={i18n.t('userModal.form.startWork')}
                                ampm={false}
                                enableAccessibleFieldDOMStructure={false}
                                slots={{ textField: TextField }}
                                slotProps={{
                                  textField: {
                                    variant: 'outlined',
                                    margin: 'dense',
                                    fullWidth: true,
                                    name: 'startWork',
                                    error:
                                      touched.startWork &&
                                      Boolean(errors.startWork),
                                    helperText:
                                      touched.startWork && errors.startWork,
                                    sx: classes.timePickerField,
                                    InputLabelProps: { shrink: true },
                                    inputProps: { step: 600 },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, xl: 6 }}>
                            <LocalizationProvider
                              dateAdapter={AdapterMoment}
                              adapterLocale='pt-br'
                            >
                              <TimePicker
                                value={
                                  values.endWork
                                    ? moment(values.endWork, 'HH:mm')
                                    : moment('23:59', 'HH:mm')
                                }
                                onChange={(newValue) => {
                                  setFieldValue(
                                    'endWork',
                                    newValue
                                      ? moment(newValue).format('HH:mm')
                                      : '23:59',
                                  );
                                }}
                                label={i18n.t('userModal.form.endWork')}
                                ampm={false}
                                enableAccessibleFieldDOMStructure={false}
                                slots={{ textField: TextField }}
                                slotProps={{
                                  textField: {
                                    variant: 'outlined',
                                    margin: 'dense',
                                    fullWidth: true,
                                    name: 'endWork',
                                    error:
                                      touched.endWork &&
                                      Boolean(errors.endWork),
                                    helperText:
                                      touched.endWork && errors.endWork,
                                    sx: classes.timePickerField,
                                    InputLabelProps: { shrink: true },
                                    inputProps: { step: 600 },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      )}
                    />

                    <Field
                      as={TextField}
                      label={i18n.t('userModal.form.farewellMessage')}
                      type='farewellMessage'
                      multiline
                      rows={4}
                      fullWidth
                      name='farewellMessage'
                      error={
                        touched.farewellMessage &&
                        Boolean(errors.farewellMessage)
                      }
                      helperText={
                        touched.farewellMessage && errors.farewellMessage
                      }
                      variant='outlined'
                      margin='dense'
                      sx={theme.custom.textField}
                    />

                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <FormControl
                          variant='outlined'
                          sx={classes.maxWidth}
                          margin='dense'
                          fullWidth
                        >
                          <>
                            <InputLabel sx={theme.custom.label}>
                              {i18n.t('userModal.form.defaultTheme')}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t('userModal.form.defaultTheme')}
                              name='defaultTheme'
                              type='defaultTheme'
                              required
                              sx={theme.custom.textField}
                              MenuProps={theme.custom.menuProps}
                            >
                              <MenuItem value='light'>
                                {i18n.t('userModal.form.defaultThemeLight')}
                              </MenuItem>
                              <MenuItem value='dark'>
                                {i18n.t('userModal.form.defaultThemeDark')}
                              </MenuItem>
                            </Field>
                          </>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <FormControl
                          variant='outlined'
                          sx={classes.maxWidth}
                          margin='dense'
                          fullWidth
                        >
                          <>
                            <InputLabel sx={theme.custom.label}>
                              {i18n.t('userModal.form.defaultMenu')}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t('userModal.form.defaultMenu')}
                              name='defaultMenu'
                              type='defaultMenu'
                              required
                              sx={theme.custom.textField}
                              MenuProps={theme.custom.menuProps}
                            >
                              <MenuItem value={'open'}>
                                {i18n.t('userModal.form.defaultMenuOpen')}
                              </MenuItem>
                              <MenuItem value={'closed'}>
                                {i18n.t('userModal.form.defaultMenuClosed')}
                              </MenuItem>
                            </Field>
                          </>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </TabPanel>
                  <TabPanel
                    sx={classes.container}
                    value={tab}
                    name={'permissions'}
                  >
                    <Can
                      role={loggedInUser.profile}
                      perform='user-modal:editProfile'
                      yes={() => (
                        <>
                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant='outlined'
                                sx={classes.maxWidth}
                                margin='dense'
                                fullWidth
                              >
                                <>
                                  <InputLabel sx={theme.custom.label}>
                                    {i18n.t('userModal.form.allTicket')}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t('userModal.form.allTicket')}
                                    name='allTicket'
                                    type='allTicket'
                                    required
                                    sx={theme.custom.textField}
                                    MenuProps={theme.custom.menuProps}
                                  >
                                    <MenuItem value='enable'>
                                      {i18n.t('userModal.form.allTicketEnable')}
                                    </MenuItem>
                                    <MenuItem value='disable'>
                                      {i18n.t(
                                        'userModal.form.allTicketDisable',
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant='outlined'
                                sx={classes.maxWidth}
                                margin='dense'
                                fullWidth
                              >
                                <>
                                  <InputLabel sx={theme.custom.label}>
                                    {i18n.t('userModal.form.allowGroup')}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t('userModal.form.allowGroup')}
                                    name='allowGroup'
                                    type='allowGroup'
                                    required
                                    sx={theme.custom.textField}
                                    MenuProps={theme.custom.menuProps}
                                  >
                                    <MenuItem value={true}>
                                      {i18n.t('userModal.form.allTicketEnable')}
                                    </MenuItem>
                                    <MenuItem value={false}>
                                      {i18n.t(
                                        'userModal.form.allTicketDisable',
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>
                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant='outlined'
                                sx={classes.maxWidth}
                                margin='dense'
                                fullWidth
                              >
                                <>
                                  <InputLabel sx={theme.custom.label}>
                                    {i18n.t('userModal.form.allHistoric')}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t('userModal.form.allHistoric')}
                                    name='allHistoric'
                                    type='allHistoric'
                                    required
                                    sx={theme.custom.textField}
                                    MenuProps={theme.custom.menuProps}
                                  >
                                    <MenuItem value='disabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricDisabled',
                                      )}
                                    </MenuItem>
                                    <MenuItem value='enabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricEnabled',
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant='outlined'
                                sx={classes.maxWidth}
                                margin='dense'
                                fullWidth
                              >
                                <>
                                  <InputLabel sx={theme.custom.label}>
                                    {i18n.t('userModal.form.allUserChat')}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t('userModal.form.allUserChat')}
                                    name='allUserChat'
                                    type='allUserChat'
                                    required
                                    sx={theme.custom.textField}
                                    MenuProps={theme.custom.menuProps}
                                  >
                                    <MenuItem value='disabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricDisabled',
                                      )}
                                    </MenuItem>
                                    <MenuItem value='enabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricEnabled',
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>
                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant='outlined'
                                sx={classes.maxWidth}
                                margin='dense'
                                fullWidth
                              >
                                <>
                                  <InputLabel sx={theme.custom.label}>
                                    {i18n.t(
                                      'userModal.form.userClosePendingTicket',
                                    )}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      'userModal.form.userClosePendingTicket',
                                    )}
                                    name='userClosePendingTicket'
                                    type='userClosePendingTicket'
                                    required
                                    sx={theme.custom.textField}
                                    MenuProps={theme.custom.menuProps}
                                  >
                                    <MenuItem value='disabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricDisabled',
                                      )}
                                    </MenuItem>
                                    <MenuItem value='enabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricEnabled',
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant='outlined'
                                sx={classes.maxWidth}
                                margin='dense'
                                fullWidth
                              >
                                <>
                                  <InputLabel sx={theme.custom.label}>
                                    {i18n.t('userModal.form.allowConnections')}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      'userModal.form.allowConnections',
                                    )}
                                    name='allowConnections'
                                    type='allowConnections'
                                    required
                                    sx={theme.custom.textField}
                                    MenuProps={theme.custom.menuProps}
                                  >
                                    <MenuItem value='disabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricDisabled',
                                      )}
                                    </MenuItem>
                                    <MenuItem value='enabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricEnabled',
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>
                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant='outlined'
                                sx={classes.maxWidth}
                                margin='dense'
                                fullWidth
                              >
                                <>
                                  <InputLabel sx={theme.custom.label}>
                                    {i18n.t('userModal.form.showDashboard')}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      'userModal.form.showDashboard',
                                    )}
                                    name='showDashboard'
                                    type='showDashboard'
                                    required
                                    sx={theme.custom.textField}
                                    MenuProps={theme.custom.menuProps}
                                  >
                                    <MenuItem value='disabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricDisabled',
                                      )}
                                    </MenuItem>
                                    <MenuItem value='enabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricEnabled',
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant='outlined'
                                sx={classes.maxWidth}
                                margin='dense'
                                fullWidth
                              >
                                <>
                                  <InputLabel sx={theme.custom.label}>
                                    {i18n.t('userModal.form.allowRealTime')}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      'userModal.form.allowRealTime',
                                    )}
                                    name='allowRealTime'
                                    type='allowRealTime'
                                    required
                                    sx={theme.custom.textField}
                                    MenuProps={theme.custom.menuProps}
                                  >
                                    <MenuItem value='disabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricDisabled',
                                      )}
                                    </MenuItem>
                                    <MenuItem value='enabled'>
                                      {i18n.t(
                                        'userModal.form.allHistoricEnabled',
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    />
                  </TabPanel>
                </DialogContent>
              </Paper>
              <DialogActions sx={theme.custom.dialogColor}>
                <Button
                  onClick={handleClose}
                  sx={theme.custom.cancelButton}
                  disabled={isSubmitting}
                  variant='outlined'
                >
                  {i18n.t('userModal.buttons.cancel')}
                </Button>
                <Button
                  type='submit'
                  disabled={isSubmitting}
                  variant='outlined'
                  sx={theme.custom.confirmButton}
                >
                  {userId
                    ? `${i18n.t('userModal.buttons.okEdit')}`
                    : `${i18n.t('userModal.buttons.okAdd')}`}
                  {isSubmitting && (
                    <CircularProgress size={24} sx={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default UserModal;
